import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { graphql, useFragment } from 'react-relay';

import type { FeeVatBasis } from '@/utils/fee-maths';

import type { DirectoryAgentFeeTable_agent$key } from './__generated__/DirectoryAgentFeeTable_agent.graphql';
import DirectorySourceLinks from './DirectorySourceLinks';
import {
  buildDirectoryAgentFeeTable,
  standardFeeCount,
} from '../utils/directory-agent-fee-table';
import { directoryBranchSectionIds } from '../utils/directory-branch-sections';

type DirectoryAgentFeeTableProps = {
  agent: DirectoryAgentFeeTable_agent$key;
};

const vatBasisOptions: { value: FeeVatBasis; label: string }[] = [
  { value: 'incVat', label: 'Inc VAT' },
  { value: 'exVat', label: 'Ex VAT' },
];

const DirectoryAgentFeeTable = ({
  agent: agentKey,
}: DirectoryAgentFeeTableProps) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryAgentFeeTable_agent on LandlordDirectoryAgent {
        name
        fees {
          management {
            amountPence
            percent
            sourceUrls
          }
          rentCollection {
            amountPence
            percent
            sourceUrls
          }
          tenantFind {
            amountPence
            percent
            sourceUrls
          }
          letOnly {
            amountPence
            percent
            sourceUrls
          }
          renewal {
            amountPence
            percent
            sourceUrls
          }
          inventory {
            amountPence
            percent
            sourceUrls
          }
          managementWithTenantInPlace {
            amountPence
            percent
            sourceUrls
          }
          rentCollectionWithTenantInPlace {
            amountPence
            percent
            sourceUrls
          }
          otherFees {
            label
            amountPence
            percent
            sourceUrls
          }
          feesIncludeVat
        }
        sources {
          feesIncludeVat
        }
      }
    `,
    agentKey,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [vatBasis, setVatBasis] = useState<FeeVatBasis>('incVat');

  const table = buildDirectoryAgentFeeTable({
    fees: {
      feesIncludeVat: agent.fees?.feesIncludeVat,
      management: agent.fees?.management
        ? {
            amountPence: agent.fees.management.amountPence,
            percent: agent.fees.management.percent,
            sourceUrls: agent.fees.management.sourceUrls,
          }
        : agent.fees?.management,
      rentCollection: agent.fees?.rentCollection
        ? {
            amountPence: agent.fees.rentCollection.amountPence,
            percent: agent.fees.rentCollection.percent,
            sourceUrls: agent.fees.rentCollection.sourceUrls,
          }
        : agent.fees?.rentCollection,
      tenantFind: agent.fees?.tenantFind
        ? {
            amountPence: agent.fees.tenantFind.amountPence,
            percent: agent.fees.tenantFind.percent,
            sourceUrls: agent.fees.tenantFind.sourceUrls,
          }
        : agent.fees?.tenantFind,
      letOnly: agent.fees?.letOnly
        ? {
            amountPence: agent.fees.letOnly.amountPence,
            percent: agent.fees.letOnly.percent,
            sourceUrls: agent.fees.letOnly.sourceUrls,
          }
        : agent.fees?.letOnly,
      renewal: agent.fees?.renewal
        ? {
            amountPence: agent.fees.renewal.amountPence,
            percent: agent.fees.renewal.percent,
            sourceUrls: agent.fees.renewal.sourceUrls,
          }
        : agent.fees?.renewal,
      inventory: agent.fees?.inventory
        ? {
            amountPence: agent.fees.inventory.amountPence,
            percent: agent.fees.inventory.percent,
            sourceUrls: agent.fees.inventory.sourceUrls,
          }
        : agent.fees?.inventory,
      managementWithTenantInPlace: agent.fees?.managementWithTenantInPlace
        ? {
            amountPence: agent.fees.managementWithTenantInPlace.amountPence,
            percent: agent.fees.managementWithTenantInPlace.percent,
            sourceUrls: agent.fees.managementWithTenantInPlace.sourceUrls,
          }
        : agent.fees?.managementWithTenantInPlace,
      rentCollectionWithTenantInPlace: agent.fees
        ?.rentCollectionWithTenantInPlace
        ? {
            amountPence: agent.fees.rentCollectionWithTenantInPlace.amountPence,
            percent: agent.fees.rentCollectionWithTenantInPlace.percent,
            sourceUrls: agent.fees.rentCollectionWithTenantInPlace.sourceUrls,
          }
        : agent.fees?.rentCollectionWithTenantInPlace,
      otherFees: agent.fees?.otherFees?.map((fee) =>
        fee == null
          ? fee
          : {
              label: fee.label,
              amountPence: fee.amountPence,
              percent: fee.percent,
              sourceUrls: fee.sourceUrls,
            },
      ),
    },
    vatBasis,
  });

  const agentName = agent.name ?? 'this agent';
  const feesIncludeVat = agent.fees?.feesIncludeVat;
  const vatSources = agent.sources?.feesIncludeVat;

  if (!table.hasAnyFee) {
    return (
      <p className="m-0 text-sm text-ink-muted">
        None of the {standardFeeCount} standard fees is published.
      </p>
    );
  }

  const unpublishedCount = table.unpublishedCount;
  const toggleDesktopLabel = isOpen
    ? 'Hide all fees'
    : `See all fees (${unpublishedCount} not published)`;

  const handleToggle = () => {
    setIsOpen((open) => !open);
  };

  return (
    <div className="flex flex-col gap-4">
      <button
        aria-controls={directoryBranchSectionIds.fees}
        aria-expanded={isOpen}
        className="inline-flex min-h-11 w-full items-center justify-between gap-2 border-0 border-t border-line-subtle bg-transparent pt-2.5 text-sm font-medium text-ink sm:mt-0 sm:inline-flex sm:w-auto sm:justify-start sm:gap-2 sm:border-0 sm:pt-0"
        onClick={handleToggle}
        type="button"
      >
        <span className="sm:hidden">All fees</span>
        <span className="hidden sm:inline">{toggleDesktopLabel}</span>
        <span className="font-normal text-ink-muted sm:hidden">
          {unpublishedCount} not published · {isOpen ? 'Hide' : 'Show'}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`size-4 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none sm:order-last ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <section
        aria-label="Full tariff"
        className="surface-card flex flex-col gap-5 p-5 md:p-6"
        hidden={!isOpen}
        id={directoryBranchSectionIds.fees}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="m-0 text-xl font-medium tracking-[-0.02em]">
              Full tariff: every fee, set out the same way for every agent
            </h2>
            <p className="m-0 mt-1 text-[13px] text-ink-muted">
              {table.publishedCount} of {standardFeeCount} standard fees
              published · not yet confirmed by {agentName}
              {feesIncludeVat == null ? (
                <> · VAT not stated: figures as published</>
              ) : null}
              {vatSources?.length ? (
                <>
                  {' '}
                  <DirectorySourceLinks label="VAT" urls={vatSources} />
                </>
              ) : null}
            </p>
          </div>
          {feesIncludeVat != null ? (
            <div
              aria-label="Show fees"
              className="inline-flex gap-1 rounded-full bg-chip p-1"
              role="group"
            >
              {vatBasisOptions.map((option) => {
                const isSelected = vatBasis === option.value;

                return (
                  <button
                    aria-pressed={isSelected}
                    className="h-9 rounded-full px-3.5 text-[13px] font-medium transition-[background-color,box-shadow] duration-150 ease-out aria-pressed:bg-white aria-pressed:shadow-[0_1px_2px_rgb(14_14_12/0.12)] motion-reduce:transition-none"
                    key={option.value}
                    onClick={() => setVatBasis(option.value)}
                    type="button"
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-5">
          {table.groups.map((group) => (
            <div key={group.id}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-ink pb-2">
                <h3 className="m-0 text-[15px] font-semibold">{group.title}</h3>
                <span className="text-xs text-ink-muted">{group.note}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="mt-0 w-full min-w-[280px] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-xs text-ink-muted">
                      <th className="py-2 pr-3 font-medium" scope="col">
                        Fee
                      </th>
                      <th
                        className="py-2 pl-3 text-right font-medium tabular-nums"
                        scope="col"
                      >
                        {table.vatAmountColumnLabel}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row) => (
                      <tr
                        className="border-t border-line-subtle align-top"
                        key={`${group.id}-${row.label}`}
                      >
                        <td className="py-2.5 pr-3">
                          <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:flex-wrap sm:items-center">
                            <span className="font-medium">{row.label}</span>
                            {row.sourceUrls.length > 0 ? (
                              <DirectorySourceLinks
                                label={row.label}
                                urls={row.sourceUrls}
                              />
                            ) : null}
                          </div>
                        </td>
                        <td
                          className={`py-2.5 pl-3 text-right font-semibold tabular-nums ${row.isPublished ? 'text-ink' : 'text-ink-muted'}`}
                        >
                          {row.amountLabel}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        {unpublishedCount > 0 ? (
          <div className="flex flex-col gap-3 rounded-2xl bg-caution-tint p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:p-5">
            <div>
              <p className="m-0 font-semibold text-ink">
                {unpublishedCount} fees aren&apos;t published
              </p>
              <p className="m-0 mt-1 text-sm text-ink-soft">
                Ask for these in writing before you sign, or let us get them for
                you.
              </p>
            </div>
            <Link
              className="button-primary inline-flex h-12 shrink-0 items-center justify-center rounded-full px-5 text-[15px] no-underline sm:bg-ink sm:text-white sm:hover:bg-ink/90"
              to="/start"
            >
              Get a full quote
            </Link>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default DirectoryAgentFeeTable;
